
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { UserReview } from '@/hooks/useUserReview';

const reviewSchema = z.object({
  rating: z.number().min(1).max(5),
  comment: z.string().max(500).optional(),
});

type ReviewFormValues = z.infer<typeof reviewSchema>;

interface ReviewFormProps {
  onSubmit: (values: { rating: number; comment?: string }) => void;
  isSubmitting: boolean;
  existingReview?: UserReview | null;
  onCancel: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({ 
  onSubmit, 
  isSubmitting, 
  existingReview,
  onCancel 
}) => {
  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      rating: existingReview?.rating || 0,
      comment: existingReview?.comment || '',
    },
  });

  const [hoverRating, setHoverRating] = React.useState<number | null>(null);
  const selectedRating = form.watch('rating');

  return (
    <div className="bg-white rounded-lg p-6 shadow-md">
      <h3 className="text-xl font-semibold mb-4">
        {existingReview ? 'Ndrysho Vlerësimin Tënd' : 'Shkruaj një Vlerësim'}
      </h3>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="rating"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vlerësimi</FormLabel>
                <FormControl>
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <Star
                        key={rating}
                        size={24}
                        className={`cursor-pointer ${
                          (hoverRating !== null ? hoverRating >= rating : selectedRating >= rating)
                            ? 'fill-amber-400 text-amber-400'
                            : 'text-gray-300'
                        }`}
                        onClick={() => field.onChange(rating)}
                        onMouseEnter={() => setHoverRating(rating)}
                        onMouseLeave={() => setHoverRating(null)}
                      />
                    ))}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Komenti (Opsional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Ndaj përvojën tënde me këtë përdorues..."
                    className="resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end space-x-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
            >
              Anulo
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !selectedRating}
              className="bg-brand-500 hover:bg-brand-600"
            >
              {isSubmitting 
                ? 'Duke dërguar...' 
                : existingReview 
                  ? 'Përditëso Vlerësimin' 
                  : 'Dërgo Vlerësimin'
              }
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ReviewForm;
